import React, { useState } from 'react';
import { FaCalculator, FaRupeeSign, FaPercentage, FaInfoCircle } from 'react-icons/fa';

const SellerPricing = () => {
    const [activeTab, setActiveTab] = useState('calculator');
    const [costPrice, setCostPrice] = useState('');
    const [marginPercent, setMarginPercent] = useState(20);
    const [shippingCost, setShippingCost] = useState(50);
    const [platformFee, setPlatformFee] = useState(10);

    const calculatePrice = () => {
        const cost = parseFloat(costPrice) || 0;
        const margin = (cost * marginPercent) / 100;
        const sellingPrice = cost + margin + shippingCost;
        const platformDeduction = (sellingPrice * platformFee) / 100;
        const netEarnings = sellingPrice - platformDeduction - shippingCost;
        return {
            sellingPrice: sellingPrice.toFixed(2),
            platformDeduction: platformDeduction.toFixed(2),
            netEarnings: netEarnings.toFixed(2),
            margin: margin.toFixed(2),
        };
    };

    const priceData = calculatePrice();

    // Mock recommended prices
    const recommendations = [
        { product: 'Handwoven Silk Saree', currentPrice: 4599, recommendedPrice: 4299, change: -6.5, reason: 'Similar products priced lower' },
        { product: 'Brass Decorative Lamp', currentPrice: 1899, recommendedPrice: 2099, change: 10.5, reason: 'High demand, low stock' },
        { product: 'Pattachitra Painting', currentPrice: 2499, recommendedPrice: 2499, change: 0, reason: 'Optimal pricing' },
        { product: 'Silver Ankle Bracelet', currentPrice: 1299, recommendedPrice: 1199, change: -7.7, reason: 'Competitive pressure' },
    ];

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Pricing Tools</h1>
                <p className="text-gray-600">Calculate optimal prices and get AI-powered recommendations</p>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6">
                <button
                    onClick={() => setActiveTab('calculator')}
                    className={`px-6 py-3 rounded-lg font-semibold transition flex items-center gap-2 ${activeTab === 'calculator'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                >
                    <FaCalculator /> Price Calculator
                </button>
                <button
                    onClick={() => setActiveTab('recommendation')}
                    className={`px-6 py-3 rounded-lg font-semibold transition flex items-center gap-2 ${activeTab === 'recommendation'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                >
                    <FaPercentage /> Price Recommendations
                </button>
            </div>

            {/* Calculator Tab */}
            {activeTab === 'calculator' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Input Section */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Enter Product Details</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Cost Price (₹)</label>
                                <div className="relative">
                                    <FaRupeeSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="number"
                                        value={costPrice}
                                        onChange={(e) => setCostPrice(e.target.value)}
                                        placeholder="Enter your cost price"
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Profit Margin: {marginPercent}%
                                </label>
                                <input
                                    type="range"
                                    min="5"
                                    max="50"
                                    value={marginPercent}
                                    onChange={(e) => setMarginPercent(parseInt(e.target.value))}
                                    className="w-full"
                                />
                                <div className="flex justify-between text-xs text-gray-500">
                                    <span>5%</span>
                                    <span>25%</span>
                                    <span>50%</span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Shipping Cost (₹)</label>
                                <input
                                    type="number"
                                    value={shippingCost}
                                    onChange={(e) => setShippingCost(parseFloat(e.target.value) || 0)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Platform Fee: {platformFee}%
                                </label>
                                <input
                                    type="range"
                                    min="5"
                                    max="20"
                                    value={platformFee}
                                    onChange={(e) => setPlatformFee(parseInt(e.target.value))}
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Results Section */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Pricing Breakdown</h2>

                        <div className="space-y-4">
                            <div className="flex justify-between items-center py-3 border-b">
                                <span className="text-gray-600">Cost Price</span>
                                <span className="font-semibold">₹{parseFloat(costPrice || 0).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center py-3 border-b">
                                <span className="text-gray-600">Profit Margin ({marginPercent}%)</span>
                                <span className="font-semibold text-green-600">+ ₹{priceData.margin}</span>
                            </div>
                            <div className="flex justify-between items-center py-3 border-b">
                                <span className="text-gray-600">Shipping</span>
                                <span className="font-semibold">+ ₹{shippingCost.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center py-3 border-b bg-blue-50 -mx-6 px-6">
                                <span className="font-semibold text-blue-800">Selling Price</span>
                                <span className="text-xl font-bold text-blue-600">₹{priceData.sellingPrice}</span>
                            </div>
                            <div className="flex justify-between items-center py-3 border-b">
                                <span className="text-gray-600">Platform Fee ({platformFee}%)</span>
                                <span className="font-semibold text-red-500">- ₹{priceData.platformDeduction}</span>
                            </div>
                            <div className="flex justify-between items-center py-3 bg-green-50 -mx-6 px-6 rounded-b-lg">
                                <span className="font-semibold text-green-800">Your Net Earnings</span>
                                <span className="text-xl font-bold text-green-600">₹{priceData.netEarnings}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Recommendations Tab */}
            {activeTab === 'recommendation' && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="p-4 border-b bg-blue-50">
                        <div className="flex items-center gap-2 text-blue-800">
                            <FaInfoCircle />
                            <span className="text-sm">AI-powered price recommendations based on market trends and competitor analysis</span>
                        </div>
                    </div>
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Price</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recommended</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Change</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {recommendations.map((item, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{item.product}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">₹{item.currentPrice}</td>
                                    <td className="px-6 py-4 whitespace-nowrap font-semibold text-blue-600">₹{item.recommendedPrice}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 rounded text-sm font-medium ${item.change > 0 ? 'bg-green-100 text-green-800' :
                                                item.change < 0 ? 'bg-red-100 text-red-800' :
                                                    'bg-gray-100 text-gray-800'
                                            }`}>
                                            {item.change > 0 ? '+' : ''}{item.change}%
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.reason}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition">
                                            Apply
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SellerPricing;
